import React, { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import createStudyMergeToast from '../StudyMerge/StudyMergeToast';
import { SimplifiedStudy } from '../types';
import styles from './MergeStudiesDialog.module.scss';
import {
  Button,
  Link,
  Message,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioButton,
  RadioGroup,
  Text,
} from 'components/core';
import { Stack } from 'components/layout';
import { completeStudyMerge } from 'query/review/StudyMerge';

type MergeStudiesDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  reviewId: string;
  studies: SimplifiedStudy[];
  selectedStudyIds: number[];
  onSuccess: () => void;
};

export const MergeStudiesDialog = ({
  isOpen,
  onClose,
  reviewId,
  studies,
  selectedStudyIds,
  onSuccess,
}: MergeStudiesDialogProps) => {
  const [selectedStudyId, setSelectedStudyId] = useState<string>('');

  // Filter studies to only include selected ones when dialog is open
  const selectedStudies = studies.filter((study) =>
    selectedStudyIds.includes(study.id)
  );

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: (selectedId: number) => {
      const studyIds = selectedStudies.map((s) => s.id);
      return completeStudyMerge(reviewId, studyIds, selectedId);
    },
    onSuccess: (response) => {
      if (response.wasMerged) {
        const studiesCount = response.studiesCount;
        createStudyMergeToast({
          toastMessage: `${studiesCount} studies merged successfully`,
          type: 'success',
        });
        onClose();
        onSuccess();
      } else {
        createStudyMergeToast({
          toastMessage:
            'Merge was not possible. Another reviewer may have changed the studies. Please refresh the page and try again.',
          type: 'danger',
        });
      }
    },
    onError: (error) => {
      createStudyMergeToast({
        toastMessage:
          error instanceof Error
            ? error.message
            : 'An error occurred. Please try again or refresh the page.',
        type: 'danger',
      });
    },
  });

  // Auto-select the first study when dialog opens
  useEffect(() => {
    if (isOpen && selectedStudies.length > 0 && !selectedStudyId) {
      setSelectedStudyId(selectedStudies[0].id.toString());
    }
  }, [isOpen, selectedStudies, selectedStudyId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedStudyId('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!selectedStudyId) return;
    mutate(Number(selectedStudyId));
  };

  const handleStudyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStudyId(event.target.value);
  };

  const renderContent = () => {
    // Don't render the radio group until we have a selected value
    // This ensures defaultValue is set correctly on first render
    if (!selectedStudyId && selectedStudies.length > 0) {
      return null;
    }

    return (
      <RadioGroup
        name="selected_study_id"
        ariaLabel="Select primary study"
        defaultValue={selectedStudyId}
        onChange={handleStudyChange}
        layout="Stack"
        layoutProps={{ spacing: 3 }}
      >
        <Stack spacing={5}>
          <Message variant="prominent" dismissible={false} type="info">
            <Text>
              The primary study is usually the most complete reference. All
              other references will be listed below the primary study.
            </Text>
            <Link
              to="https://support.covidence.org/help/merging-and-unmerging-studies"
              external
            >
              Learn more
            </Link>
          </Message>
          <span className={styles.studyOption}></span>
          {selectedStudies.map((study) => {
            const descriptionId = `merge-study-${study.id}-title`;
            return (
              <React.Fragment key={study.id}>
                <span>
                  <RadioButton
                    value={study.id.toString()}
                    size="sm"
                    aria-describedby={descriptionId}
                  >
                    #{study.unique_index} {study.study_id}
                  </RadioButton>
                  <Text
                    id={descriptionId}
                    size="sm"
                    variant="weak"
                    className={styles.studyTitle}
                  >
                    {study.title || <i>[Untitled reference]</i>}
                  </Text>
                </span>
                <span className={styles.studyOption}></span>
              </React.Fragment>
            );
          })}
        </Stack>
      </RadioGroup>
    );
  };

  const canSubmit = selectedStudyId !== '' && !isSubmitting;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader>Assign a primary study</ModalHeader>
      <ModalBody>{renderContent()}</ModalBody>
      <span className={styles.modalFooterSpacer} />
      <ModalFooter>
        <Button
          type="neutral"
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="brand"
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={isSubmitting}
        >
          Merge {selectedStudies.length} studies
        </Button>
      </ModalFooter>
    </Modal>
  );
};
